/**
 * Room tone synthesis.
 *
 * A recording ships a ~4 KB spectrum of its own silence. From that we can generate as
 * much of that room as we like, and — this is the surprising result from the blind
 * listening test — what we generate is *preferred* to the real tape, because the real
 * tape has sniffs and chair movements in it and this does not.
 *
 * The method is FIR-filtered white noise: build a 4096-tap filter from the measured
 * magnitude spectrum, run Gaussian noise through it. Two properties matter.
 *
 *   * It is mathematically aperiodic. Measured self-correlation 0.016 against 0.216 for
 *     real tape. Nothing loops, at any timescale, which matters because a meditator in
 *     headphones for forty minutes is the worst-case listener for periodicity.
 *   * It has no amplitude modulation. The first attempt at this used overlap-add with a
 *     Hann window, which sums power as w² and injects 3 dB of modulation at the hop
 *     rate. It matched the spectrum to half a decibel and was unlistenable. There is no
 *     framing here at all, so the failure mode cannot recur — but `measureAmDepth`
 *     exists to keep checking.
 *
 * Rendering happens in chunks through an OfflineAudioContext, each chunk fed fresh
 * noise, so memory stays at one chunk regardless of how long the silence runs.
 */

import { fft, hann, irfftZeroPhase } from './fft.ts';

export const FIR_TAPS = 4096;

/** Seconds per rendered chunk. Defined in ./parts, which the pure scheduler can reach. */
export { CHUNK_SEC } from './parts.ts';

/** Equal-power crossfade between consecutive chunks. Measured invisible at this length. */
export const CHUNK_FADE_SEC = 0.25;

// ---------------------------------------------------------------- profile

/**
 * A recording's silence, as 2049 fp16 magnitudes from a 4096-point transform.
 * This is the entire payload needed to reproduce the room.
 */
export class ToneProfile {
  readonly mag: Float32Array;
  readonly rms: number;

  // Fields are assigned explicitly rather than declared as constructor parameter properties:
  // Node's type-stripping loader refuses those outright, and tools/mobile/emit-tone-goldens.mjs
  // imports this file directly so the native goldens come from the shipped code.
  private constructor(mag: Float32Array, rms: number) {
    this.mag = mag;
    this.rms = rms;
  }

  static fromBytes(buf: ArrayBuffer): ToneProfile {
    const raw = new Uint16Array(buf);
    const mag = new Float32Array(raw.length);
    for (let i = 0; i < raw.length; i++) mag[i] = f16(raw[i]);
    // Parseval: the time-domain RMS implied by this spectrum, used to set output level.
    let power = 0;
    for (let i = 0; i < mag.length; i++) {
      const w = i === 0 || i === mag.length - 1 ? 1 : 2;
      power += w * mag[i] * mag[i];
    }
    return new ToneProfile(mag, Math.sqrt(power) / mag.length);
  }

  /** A profile part-way between this one and another. Used to bridge across a gap. */
  lerp(other: ToneProfile, t: number): ToneProfile {
    const mag = new Float32Array(this.mag.length);
    for (let i = 0; i < mag.length; i++) {
      mag[i] = this.mag[i] * (1 - t) + other.mag[i] * t;
    }
    return new ToneProfile(mag, this.rms * (1 - t) + other.rms * t);
  }

  /** The 4096-tap impulse response: zero-phase irfft, rolled to centre, Hann-windowed. */
  impulseResponse(taps = FIR_TAPS): Float32Array<ArrayBuffer> {
    const full = irfftZeroPhase(this.mag);
    const n = full.length;
    const w = hann(taps);
    const out = new Float32Array(taps);
    // Roll by n/2 so the symmetric response is centred, then take the first `taps`.
    for (let i = 0; i < taps; i++) {
      out[i] = full[(i + n - taps / 2) % n] * w[i];
    }
    return out;
  }
}

/** IEEE 754 half-precision to float. */
function f16(h: number): number {
  const s = (h & 0x8000) >> 15;
  const e = (h & 0x7c00) >> 10;
  const f = h & 0x03ff;
  if (e === 0) return (s ? -1 : 1) * Math.pow(2, -14) * (f / 1024);
  if (e === 0x1f) return f ? NaN : (s ? -Infinity : Infinity);
  return (s ? -1 : 1) * Math.pow(2, e - 15) * (1 + f / 1024);
}

// ---------------------------------------------------------------- noise

/**
 * Gaussian white noise, Box-Muller. Uniform noise would give the same spectrum but the
 * wrong marginal distribution, and the gate that admitted this recording tested for
 * kurtosis near 3 — so the thing we generate should actually be Gaussian.
 */
export function gaussianNoise(
  n: number,
  rng: () => number = Math.random,
): Float32Array<ArrayBuffer> {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i += 2) {
    const u = Math.max(rng(), 1e-12);
    const v = rng();
    const r = Math.sqrt(-2 * Math.log(u));
    out[i] = r * Math.cos(2 * Math.PI * v);
    if (i + 1 < n) out[i + 1] = r * Math.sin(2 * Math.PI * v);
  }
  return out;
}

/** Mulberry32 — small, fast, and seedable, so a session can be reproduced exactly. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------- rendering

export interface RenderOptions {
  sampleRate: number;
  /** Linear gain applied after normalisation, to match the surrounding tape. */
  targetRms: number;
  seed?: number;
}

/**
 * Render `seconds` of tone from a profile.
 *
 * Convolution runs on a ConvolverNode, which is the platform's own FFT-based
 * implementation — a 4096-tap direct convolution in JS would be about 100 million
 * multiplies per second of output and is not worth writing.
 */
export async function renderTone(
  profile: ToneProfile,
  seconds: number,
  opts: RenderOptions,
): Promise<AudioBuffer> {
  const { sampleRate, targetRms } = opts;
  const rng = seededRandom(opts.seed ?? 1);
  const n = Math.round(seconds * sampleRate);

  // Pad by the filter length so the convolution tail is inside the render, then trim.
  const pad = FIR_TAPS;
  const ctx = new OfflineAudioContext(1, n + pad * 2, sampleRate);

  const ir = profile.impulseResponse();
  const irBuf = ctx.createBuffer(1, ir.length, sampleRate);
  irBuf.copyToChannel(ir, 0);

  const noise = ctx.createBuffer(1, n + pad * 2, sampleRate);
  noise.copyToChannel(gaussianNoise(n + pad * 2, rng), 0);

  const src = ctx.createBufferSource();
  src.buffer = noise;
  const conv = ctx.createConvolver();
  conv.buffer = irBuf;
  conv.normalize = false;      // we set level explicitly below; let the filter be the filter
  src.connect(conv).connect(ctx.destination);
  src.start();

  const rendered = await ctx.startRendering();

  // Trim the padding and normalise to the requested RMS. Normalising after the fact
  // rather than trusting the filter gain keeps this robust to profile scaling.
  const out = new AudioBuffer({ length: n, sampleRate, numberOfChannels: 1 });
  const data = rendered.getChannelData(0).subarray(pad, pad + n);
  let sum = 0;
  for (let i = 0; i < n; i++) sum += data[i] * data[i];
  const rms = Math.sqrt(sum / n) || 1e-20;
  const g = targetRms / rms;
  const scaled = new Float32Array(new ArrayBuffer(n * 4));
  for (let i = 0; i < n; i++) scaled[i] = data[i] * g;
  out.copyToChannel(scaled, 0);
  return out;
}

// ---------------------------------------------------------------- QA

/**
 * Amplitude-modulation depth in the 3–30 Hz band, as peak-to-median of the envelope
 * spectrum.
 *
 * This metric exists because PSD match and kurtosis are both marginal statistics and
 * neither of them can see modulation over time. The first synthesis in this project
 * matched the spectrum to 0.50 dB and sounded, in the client's words, horrific; this
 * number was 25× where real tape is 11×. Anything the client renders should measure
 * below the tape. It is cheap, so the player checks it on the first chunk of every
 * session and reports a failure rather than playing it.
 */
export function measureAmDepth(
  buf: Float32Array,
  sampleRate: number,
  lo = 3,
  hi = 30,
): { ratio: number; atHz: number } {
  const hop = Math.round(sampleRate * 0.005);      // 5 ms envelope sampling
  const nf = Math.floor(buf.length / hop);
  if (nf < 64) return { ratio: 0, atHz: 0 };

  const env = new Float64Array(nf);
  let mean = 0;
  for (let k = 0; k < nf; k++) {
    let s = 0;
    for (let i = k * hop; i < (k + 1) * hop; i++) s += buf[i] * buf[i];
    env[k] = Math.sqrt(s / hop);
    mean += env[k];
  }
  mean /= nf;
  if (mean <= 0) return { ratio: 0, atHz: 0 };

  // Next power of two, Hann-windowed, so we can reuse the same FFT.
  let n = 1;
  while (n < nf) n <<= 1;
  const re = new Float64Array(n);
  const im = new Float64Array(n);
  const w = hann(nf);
  for (let k = 0; k < nf; k++) re[k] = (env[k] / mean - 1) * w[k];

  fft(re, im);

  const binHz = 1 / (0.005 * n);
  let peak = 0;
  let atHz = 0;
  const mags: number[] = [];
  for (let k = 1; k < n / 2; k++) {
    const f = k * binHz;
    if (f > hi) break;
    const m = Math.hypot(re[k], im[k]);
    if (f > 0.5) mags.push(m);
    if (f >= lo && m > peak) { peak = m; atHz = f; }
  }
  mags.sort((a, b) => a - b);
  const median = mags.length ? mags[Math.floor(mags.length / 2)] : 1e-20;
  return { ratio: peak / (median || 1e-20), atHz };
}
